import SellingUnit from "@/components/SellingUnit";
import ShippingDetails from "@/components/checkout/ShippingDetails";
import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Loader2, PackageCheck, Truck, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang, useL } from "@/i18n";
import { api, money } from "@/lib/api";
import OrderProgress, { ShipmentTracking, OrderTimeline } from "@/components/orders/OrderProgress";
import { orderStage, paymentKey, methodLabel, stages } from "@/lib/fulfillment";

const paymentStatusColor = { paid: "bg-palm-light text-palm-500", pending: "bg-amber-100 text-amber-700", payment_pending: "bg-amber-100 text-amber-700", payment_failed: "bg-red-100 text-red-700" };
// A real, dedicated, mobile-first page for one order -- not the desktop
// Account layout squeezed down. Works two ways: a signed-in owner (cookie
// auth, no token needed) or anyone holding this order's guest access token
// (?token=...), which is how a guest checkout reaches their own order with
// no account required. See get_order() in server.py for the backend side.
export default function OrderDetail() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user } = useAuth();
  const { lang, t } = useLang();
  const L = useL();
  const [state, setState] = useState("loading"); // loading | ready | denied | notfound
  const [order, setOrder] = useState(null);

  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState("loading");
    api.get(`/orders/${orderId}`, { headers: token ? { "X-Order-Token": token } : {}, timeout: 20000, signal: controller.signal })
      .then((r) => { if (!controller.signal.aborted) { setOrder(r.data); setState("ready"); } })
      .catch((e) => { if (!controller.signal.aborted) setState(e.response?.status === 404 ? "notfound" : [401,403].includes(e.response?.status) ? "denied" : "error"); });
    return () => controller.abort();
  }, [orderId, token, revision]);

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") setRevision(v => v + 1); };
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); window.removeEventListener("pageshow", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, []);

  if (state === "loading") {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 px-4 text-navy/60" data-testid="order-detail-loading">
        <Loader2 size={28} className="animate-spin text-gold" />
        <p className="text-sm">{t("checkout.loadingOrder")}</p>
      </div>
    );
  }

  if (state === "denied" || state === "notfound" || state === "error") {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 py-20 text-center" data-testid="order-detail-error">
        <AlertTriangle size={40} className="text-sunset" />
        <h1 className="font-display text-xl font-bold text-navy">{t(state === "error" ? "orders.loadError" : "checkout.orderNotFoundTitle")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("tracking.accessHelp")}
        </p>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-3">
          <Link to="/track-order" className="btn-gold rounded-full px-6 py-3 text-sm font-bold">{t("tracking.title")}</Link>
          {state === "error" && <button onClick={() => setRevision(v => v + 1)}>{t("tracking.refresh")}</button>}
          <Link to="/shop" className="rounded-full border border-navy px-6 py-3 text-sm font-semibold text-navy">{t("checkout.continueShopping")}</Link>
        </div>
      </div>
    );
  }

  const addr = order.shipping_address;
  const isGuest = !user;

  return (
    <div className="mx-auto max-w-xl px-4 py-8 sm:py-10" data-testid="order-detail-page">
      <button className="mb-4 rounded-full border border-navy px-4 py-2 text-sm" onClick={() => setRevision(v => v + 1)}>{t("tracking.refresh")}</button>
      {/* Header card */}
      <div className="rounded-2xl border border-border bg-cream-card p-5 card-shadow">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{order.order_number}</p>
        <p className="mt-0.5 text-sm text-navy/60">{order.created_at ? new Date(order.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-US", { year: "numeric", month: "long", day: "numeric" }) : "—"}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className={`rounded-xl px-3 py-2.5 ${paymentStatusColor[order.payment_status] || "bg-gray-100 text-gray-700"}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{t("checkout.paymentStatusLabel")}</p>
            <p className="flex items-center gap-1 text-sm font-bold"><PackageCheck size={13} /> {t("orders." + paymentKey(order.payment_status))}</p>
          </div>
          <div className={`rounded-xl px-3 py-2.5 bg-gray-100 text-gray-700`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{t("checkout.orderStatusLabel")}</p>
            <p className="flex items-center gap-1 text-sm font-bold"><Truck size={13} /> {t("orders." + (stages.includes(orderStage(order)) || orderStage(order) === "cancelled" ? orderStage(order) : "unknown"))}</p>
          </div>
        </div>
      </div>

      <OrderProgress order={order} />
      <ShipmentTracking order={order} />
      <ShippingDetails order={order} />
      <OrderTimeline order={order} />

      {/* Products */}
      <div className="mt-4 rounded-2xl border border-border bg-cream-card p-5 card-shadow">
        <p className="mb-3 text-xs font-semibold uppercase text-navy/50">{t("checkout.itemsInOrder")}</p>
        <div className="space-y-3">
          {(order.items || []).map((it, i) => (
            <div key={i} className="flex items-center gap-3">
              {it.image && <img src={it.image} alt="" className="h-14 w-14 shrink-0 rounded-lg bg-cream object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="break-words font-medium text-navy">{L(it, "name")}</p>
                <SellingUnit product={it} />
                {it.selling_unit !== "marmite" && it.weight && <p className="text-xs text-muted-foreground">{it.weight}</p>}
                <p className="text-xs text-muted-foreground">{t("common.quantity")}: {it.quantity}</p>
              </div>
              <span className="shrink-0 font-semibold text-navy">{money(it.line_total)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Order summary */}
      <div className="mt-4 rounded-2xl border border-border bg-cream-card p-5 card-shadow">
        <p className="mb-3 text-xs font-semibold uppercase text-navy/50">{t("checkout.orderSummary")}</p>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between text-navy/70"><span>{t("common.subtotal")}</span><span>{money(order.subtotal)}</span></div>
          <div className="flex justify-between text-navy/70"><span>{t("common.shipping")}</span><span>{order.shipping === 0 ? t("common.free") : money(order.shipping)}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-palm-500"><span>{t("common.discount")}{order.coupon_code ? ` (${order.coupon_code})` : ""}</span><span>-{money(order.discount)}</span></div>}
          {order.tax > 0 && <div className="flex justify-between text-navy/70"><span>{t("common.tax")}</span><span>{money(order.tax)}</span></div>}
          <div className="flex justify-between border-t border-border pt-2 text-base font-bold text-navy"><span>{t("common.total")}</span><span>{money(order.total)}</span></div>
        </div>
      </div>

      {/* Shipping */}
      {addr && (
        <div className="mt-4 rounded-2xl border border-border bg-cream-card p-5 card-shadow">
          <p className="mb-2 text-xs font-semibold uppercase text-navy/50">{t("checkout.shippingTo")}</p>
          <p className="text-sm leading-relaxed text-navy/80">
            {addr.full_name}<br />
            {addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />
            {addr.city}, {addr.state} {addr.zip}<br />
            {lang === "fr" ? "États-Unis" : "United States"}
          </p>
          {order.shipping_method && (
            <p className="mt-2 text-xs text-navy/50">{t("checkout.shippingMethod")}: {methodLabel(order, t)}</p>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3">
        <Link to="/shop" className="btn-gold rounded-full py-3.5 text-center text-sm font-bold">{t("checkout.continueShopping")}</Link>
        {isGuest && (
          <Link to="/register" className="rounded-full border border-navy py-3.5 text-center text-sm font-semibold text-navy">{t("checkout.createAccount")}</Link>
        )}
      </div>
    </div>
  );
}
